"use client"

import OrLineSeparator from '../../../../../shared/components/ui/or-line-separator'
import { AUTH_PAGE_ROUTE } from '../../../../../shared/constants/pages-routes'
import { LockKeyhole } from 'lucide-react'
import Link from 'next/link'
import React, { useState } from 'react'
import GoogleAuthButton from '../../google-auth-button'
import { LoginFormType } from '../../../schemas/login.schema'
import FormTextInput from '@/shared/components/form-inputs/form-text-input'
import { useFormContext } from 'react-hook-form'
import { Button } from '@/shared/components/ui/button'
import { sendLoginOtp, VerifyOtp, verifySession, loginUser, isEmailExists } from '@/features/auth/api/auth.api'
import { getPostLoginRedirectRoute } from '@/shared/utils/auth-redirect'
import errorToast from '@/shared/lib/toast/error.toast'
import { useRouter } from 'next/navigation'
import { PAGE_ROUTES } from '@/shared/constants/pages-routes'
import { useLoginFormPrefills } from '../../../contexts/login-form-context'

type LoginMode = 'initial' | 'password' | 'otp-sent' | 'email-not-found'

function LoginForm() {
    const { prefilledRegion, prefilledRole, prefilledTeam } = useLoginFormPrefills()
    const { control, handleSubmit, watch, setValue } = useFormContext<LoginFormType>()
    const router = useRouter()
    const [mode, setMode] = useState<LoginMode>('initial')
    const [otpLoading, setOtpLoading] = useState(false)
    const [verifyLoading, setVerifyLoading] = useState(false)

    const email = watch('email')

    // Primary: "Email me a code"
    const onSendOtp = async () => {
        await handleSubmit(
            async (data) => {
                try {
                    setOtpLoading(true)
                    const res = await sendLoginOtp(data.email)
                    if (!res.success) {
                        if (res.status === 404) {
                            setMode('email-not-found')
                            return
                        }
                        errorToast(res.message || 'Failed to send code')
                        return
                    }
                    setMode('otp-sent')
                    setValue('code', '')
                } catch (e) {
                    errorToast((e as Error).message)
                } finally {
                    setOtpLoading(false)
                }
            },
            () => setOtpLoading(false),
        )()
    }

    // Verify OTP
    const onVerifyOtp = async () => {
        await handleSubmit(
            async (data) => {
                if (!data.code || data.code.length !== 6) {
                    errorToast('Please enter the 6-digit code')
                    return
                }
                try {
                    setVerifyLoading(true)
                    const res = await VerifyOtp(data.email, data.code)
                    if (!res.success) {
                        errorToast(res.message || 'Invalid or expired code')
                        return
                    }
                    const session = await verifySession()
                    let currentStep: number | undefined
                    if (session?.sub) {
                        try {
                            const { getProfile } = await import('@/features/profile/api/profile.api')
                            const profileRes = await getProfile()
                            if (profileRes.success && profileRes.data) {
                                currentStep = profileRes.data.currentStep
                            }
                        } catch {
                            /* ignore */
                        }
                    }
                    const redirectRoute = session?.role
                        ? getPostLoginRedirectRoute(session.role, null, currentStep)
                        : PAGE_ROUTES.dashboard
                    window.location.href = redirectRoute
                } catch (e) {
                    errorToast((e as Error).message)
                } finally {
                    setVerifyLoading(false)
                }
            },
            () => setVerifyLoading(false),
        )()
    }

    const onTryDifferentEmail = () => {
        setMode('initial')
        setValue('email', '')
        setValue('password', '')
        setValue('code', '')
    }

    // Password login (with Option C for email not found)
    const onPasswordLogin = async () => {
        const valid = await handleSubmit(
            async (data) => {
                if (!data.password) {
                    errorToast('Please enter your password')
                    return
                }
                try {
                    const emailCheckRes = await isEmailExists(data.email)
                    if (!emailCheckRes.success) {
                        errorToast(emailCheckRes.message || 'Failed to check email')
                        return
                    }
                    if (!emailCheckRes.data) {
                        errorToast('To create a new account please use Google login or press "Email me a code" to create an account with a one time password.')
                        setMode('email-not-found')
                        return
                    }
                    // Google-only accounts (no password) must use Google sign-in
                    // Only block when provider is google AND they have no password - accounts that were linked to Google later still have their password
                    if (emailCheckRes.provider === 'google' && emailCheckRes.hasPassword === false) {
                        errorToast('This account was created with Google. Please use "Continue with Google" to sign in.')
                        return
                    }
                    // Try password login - if user has password stored, it will succeed
                    const loginRes = await loginUser(data.email, data.password!)
                    if (!loginRes.success) {
                        // Show backend message (e.g. "use email codes" for passwordless accounts, "Invalid password" otherwise)
                        errorToast(loginRes.message || 'Invalid email or password')
                        if (emailCheckRes.hasPassword === false) {
                            setMode('initial')
                        }
                        return
                    }
                    const session = await verifySession()
                    let currentStep: number | undefined
                    if (session?.sub) {
                        try {
                            const { getProfile } = await import('@/features/profile/api/profile.api')
                            const profileRes = await getProfile()
                            if (profileRes.success && profileRes.data) {
                                currentStep = profileRes.data.currentStep
                            }
                        } catch {
                            /* ignore */
                        }
                    }
                    const redirectRoute = session?.role
                        ? getPostLoginRedirectRoute(session.role, null, currentStep)
                        : PAGE_ROUTES.dashboard
                    window.location.href = redirectRoute
                } catch (e) {
                    errorToast((e as Error).message)
                }
            },
            () => {},
        )()
    }

    const onSignUp = () => {
        if (email) {
            sessionStorage.setItem('registrationEmail', email)
            const region = prefilledRegion ?? (sessionStorage.getItem('unifiedAuthRegion') ? parseInt(sessionStorage.getItem('unifiedAuthRegion')!, 10) : undefined)
            const role = prefilledRole || (sessionStorage.getItem('unifiedAuthRole') as 'homeowner' | 'volunteer' | undefined)
            const team = prefilledTeam || sessionStorage.getItem('unifiedAuthTeam') || undefined
            if (region) sessionStorage.setItem('registrationRegion', String(region))
            if (role) sessionStorage.setItem('registrationRole', role)
            if (team) sessionStorage.setItem('registrationTeam', team)
        }
        if (prefilledRole === 'homeowner') {
            router.push(AUTH_PAGE_ROUTE.signupInto)
        } else if (prefilledRole === 'volunteer') {
            router.push(AUTH_PAGE_ROUTE.emailRegistration)
        } else {
            router.push('/role')
        }
    }

    // Email not found - Option C
    if (mode === 'email-not-found') {
        return (
            <div className="space-y-3 md:space-y-5 w-full pb-3 md:pb-5">
                <GoogleAuthButton />
                <OrLineSeparator />
                <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 p-4 space-y-4">
                    <p className="text-sm text-amber-800 dark:text-amber-200">
                        We don&apos;t have an account with that email.
                    </p>
                    <div className="flex flex-col gap-2">
                        <Button
                            type="button"
                            className="w-full"
                            onClick={onSignUp}
                        >
                            Sign up
                        </Button>
                        <Button
                            type="button"
                            variant="outline"
                            className="w-full"
                            onClick={onTryDifferentEmail}
                        >
                            Try a different email
                        </Button>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="space-y-3 md:space-y-5 w-full pb-3 md:pb-5">
            <GoogleAuthButton />
            <OrLineSeparator />

            {/* Email - always shown (or readonly when OTP sent) */}
            <FormTextInput
                inputGroupProps={{ label: 'Email Address' }}
                type="email"
                placeholder="Email*"
                name="email"
                control={control}
                disabled={mode === 'otp-sent'}
            />

            {/* Initial: OTP primary, password link */}
            {mode === 'initial' && (
                <>
                    <Button
                        type="button"
                        className="w-full"
                        onClick={onSendOtp}
                        disabled={otpLoading}
                        showLoader={otpLoading}
                    >
                        {otpLoading ? 'Sending...' : 'Email me a code'}
                    </Button>
                    <button
                        type="button"
                        className="text-sm text-muted-foreground hover:text-foreground underline w-full text-center"
                        onClick={() => setMode('password')}
                    >
                        Sign in with password instead
                    </button>
                </>
            )}

            {/* Password mode */}
            {mode === 'password' && (
                <>
                    <p className="text-xs text-muted-foreground italic">
                        Heads up: New accounts can only be created with a Google login or OTP. If you previously created a password, enjoy it while it lasts — like flip phones and fax machines.
                    </p>
                    <div className="w-full flex flex-col gap-1">
                        <FormTextInput
                            inputGroupProps={{ label: 'Password' }}
                            type="password"
                            placeholder="Password*"
                            name="password"
                            control={control}
                        />
                        <Link
                            href={AUTH_PAGE_ROUTE.forgotPassword}
                            className="text-xs flex justify-end gap-1 items-center font-semibold underline space-x-2"
                        >
                            <LockKeyhole className="w-4" />
                            Forgot Password?
                        </Link>
                    </div>
                    <div className="flex flex-col gap-2">
                        <Button
                            type="button"
                            className="w-full"
                            onClick={onPasswordLogin}
                        >
                            Sign in
                        </Button>
                        <button
                            type="button"
                            className="text-sm text-muted-foreground hover:text-foreground underline"
                            onClick={() => setMode('initial')}
                        >
                            Use a one-time code instead
                        </button>
                    </div>
                </>
            )}

            {/* OTP sent - verify step */}
            {mode === 'otp-sent' && (
                <>
                    <p className="text-sm text-muted-foreground">
                        We sent a 6-digit code to <span className="font-medium text-foreground">{email}</span>
                    </p>
                    <FormTextInput
                        inputGroupProps={{ label: 'Enter code' }}
                        type="text"
                        placeholder="000000"
                        name="code"
                        control={control}
                        maxLength={6}
                        inputMode="numeric"
                    />
                    <Button
                        type="button"
                        className="w-full"
                        onClick={onVerifyOtp}
                        disabled={verifyLoading}
                        showLoader={verifyLoading}
                    >
                        {verifyLoading ? 'Verifying...' : 'Verify'}
                    </Button>
                    <button
                        type="button"
                        className="text-sm text-muted-foreground hover:text-foreground underline w-full text-center"
                        onClick={onSendOtp}
                        disabled={otpLoading}
                    >
                        {otpLoading ? 'Sending...' : 'Send a new code'}
                    </button>
                    <button
                        type="button"
                        className="text-sm text-muted-foreground hover:text-foreground underline w-full text-center"
                        onClick={() => setMode('initial')}
                    >
                        Use a different email
                    </button>
                </>
            )}
        </div>
    )
}

export default LoginForm
